import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  untracked,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '@/app/shared/header/header.component';
import { SidebarComponent } from '@/app/shared/sidebar/sidebar.component';
import { Footer } from '@/app/shared/footer/footer';
import { DooctiContainerComponent } from '@/app/shared/doocti-container/doocti-container.component';
import { PermissionService } from '@/app/core/services/permission/permission.service';
import { environment } from '@/environments/environment';
import { DooctiService } from '@/app/core/services/doocti/doocti.service';
import { UsersService } from '@/app/core/services/users/users.service';
import { PresenceService } from '@/app/core/services/presence/presence.service';
import { FeatureAvailabilityService } from '@/app/core/services/feature-availability/feature-availability.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    HeaderComponent,
    SidebarComponent,
    Footer,
    DooctiContainerComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly dooctiService = inject(DooctiService);
  private readonly userService = inject(UsersService);
  private readonly presenceService = inject(PresenceService);
  private readonly featureAvailability = inject(FeatureAvailabilityService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  isSidebarCollapsed = false;
  isMobileSidebarOpen = false;
  showDoocti = false;

  constructor() {
    effect(() => {
      const isEnabled = this.featureAvailability.clickToCallEnabled();
      untracked(() => {
        this.syncDooctiPanel(isEnabled);
      });
    });
  }

  ngOnInit(): void {
    // Initial fetch of public feature settings
    this.featureAvailability.refresh().subscribe();

    // Listen for deferred teardown completion (e.g. after an active call ends)
    this.dooctiService.teardownCompleted$?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showDoocti = false;
      this.cdr.detectChanges();
    });

    const isAgent = this.permissionService.isAgentLogin();
    if (isAgent) {
      this.presenceService.start();
    } else {
      // Re-check profile on page load/refresh if cache was not yet populated
      this.userService.getProfile().subscribe({
        next: () => {
          if (this.permissionService.isAgentLogin()) {
            this.presenceService.start();
            this.syncDooctiPanel(this.featureAvailability.clickToCallEnabled());
          }
        },
        error: (err) => {
          console.debug('[MainLayout] Failed to load profile for presence initialization:', err);
        },
      });
    }
  }

  /**
   * Synchronize the Doocti phone panel state whenever the Click-to-Call setting changes.
   */
  private syncDooctiPanel(enabled: boolean): void {
    if (!environment.doocti?.enabled) {
      this.showDoocti = false;
      this.dooctiService?.destroy?.();
      this.cdr.detectChanges();
      return;
    }

    if (!enabled) {
      this.handleDisableDoocti();
      return;
    }

    this.handleEnableDoocti();
  }

  private handleDisableDoocti(): void {
    if (this.dooctiService?.isCallActive) {
      // Defer teardown until active call ends so WebRTC session is not severed abruptly
      this.dooctiService.teardown?.();
      return;
    }

    this.showDoocti = false;
    this.dooctiService?.destroy?.();
    this.cdr.detectChanges();
  }

  private handleEnableDoocti(): void {
    // Cancel any pending deferred teardown if setting was re-enabled
    this.dooctiService?.cancelDeferredTeardown?.();

    if (!this.permissionService.isAgentLogin()) {
      // Profile may not be loaded yet
      this.userService.getProfile().subscribe({
        next: () => {
          if (
            this.permissionService.isAgentLogin() &&
            environment.doocti?.enabled &&
            this.featureAvailability.clickToCallEnabled()
          ) {
            this.mountAndInitDoocti();
          }
        },
      });
      return;
    }

    this.mountAndInitDoocti();
  }

  private mountAndInitDoocti(): void {
    if (
      !this.permissionService.isAgentLogin() ||
      !environment.doocti?.enabled ||
      !this.featureAvailability.clickToCallEnabled()
    ) {
      return;
    }

    this.showDoocti = true;
    this.cdr.detectChanges();

    this.userService.getProfile().subscribe({
      next: (profile) => {
        if (!this.featureAvailability.clickToCallEnabled()) {
          return;
        }
        const dooctiUsername = profile.email;
        const dooctiPassword = 'Doocti@123';
        this.dooctiService.initialize(dooctiUsername, dooctiPassword);
      },
      error: (err) => {
        console.error('[MainLayout] Failed to load profile for Doocti initialization:', err);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.presenceService.stop();
  }

  toggleSidebar() {
    if (window.innerWidth < 768) {
      // Mobile behavior
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    } else {
      // Desktop behavior
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    }
  }

  closeMobileSidebar() {
    this.isMobileSidebarOpen = false;
  }
}
